import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { 
  ThumbsUp, 
  ThumbsDown, 
  CheckCircle,
  HelpCircle,
  Sparkles,
  ArrowRight,
  MessageCircle,
  Plus
} from "lucide-react";
import { useFAQ } from "@/hooks/useFAQ";

const LandingFAQ = () => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  
  const { 
    groupedFAQs, 
    loading, 
    error, 
    trackView, 
    markHelpful, 
    votedFAQs 
  } = useFAQ();

  // Get only General category FAQs
  const generalFAQs = groupedFAQs['General'] || [];

  // Handle FAQ item click (track view only on open)
  const handleFAQValueChange = async (value: string) => {
    if (value && !openItems.has(value)) {
      // Item is being opened
      setOpenItems(prev => new Set(prev).add(value));
      await trackView(value);
    } else if (!value || !openItems.has(value)) {
      // Item is being closed - don't track anything
      if (value) {
        setOpenItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(value);
          return newSet;
        });
      }
    }
  };

  // Handle helpful rating
  const handleHelpfulRating = async (faqId: string, isHelpful: boolean) => {
    await markHelpful(faqId, isHelpful);
  };

  if (loading || generalFAQs.length === 0) {
    return null; // Don't show the section if loading or no general FAQs
  }

  return (
    <div className="relative bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 sm:py-24 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes */}
        <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
        <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
        <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
        
        {/* Triangle Decorative Elements */}
        <div className="absolute top-1/4 right-10 w-0 h-0 border-l-[20px] border-r-[20px] border-b-[35px] border-l-transparent border-r-transparent border-b-jamaica-green/10 animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/3 left-16 w-0 h-0 border-l-[15px] border-r-[15px] border-b-[26px] border-l-transparent border-r-transparent border-b-jamaica-yellow/15 animate-bounce delay-1200"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(0, 116, 47, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 116, 47, 0.05) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="relative">
              <div className="w-14 h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6">
                <HelpCircle className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                <MessageCircle className="w-3 h-3 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-5 py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
              <span className="text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-jamaica-green" />
                Quick Answers
              </span>
            </div>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
              Frequently Asked Questions
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get quick answers to the most common questions about our 
            <span className="text-jamaica-yellow font-semibold"> Jamaican care packages</span> and 
            <span className="text-jamaica-green font-semibold"> services</span>
          </p>
        </motion.div>

        {/* FAQ Content */}
        <div className="max-w-4xl mx-auto">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-xl text-red-800"
            >
              {error}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-4 mb-12"
          >
            <Accordion type="single" collapsible className="space-y-3" onValueChange={handleFAQValueChange}>
              {generalFAQs.slice(0, 5).map((faq, index) => ( // Show only first 5 general FAQs
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <AccordionItem 
                    value={faq.id} 
                    className="border-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                  >
                    <AccordionTrigger className="px-6 py-6 text-left font-medium text-gray-900 hover:no-underline hover:bg-gray-50/50 transition-colors rounded-t-2xl [&[data-state=open]]:rounded-b-none">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {faq.question}
                      </h3>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="pt-4 border-t border-gray-100">
                        <div className="prose prose-gray max-w-none">
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {faq.answer}
                          </p>
                        </div>
                        
                        {/* Subtle Helpful Rating */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-gray-500">
                              Was this helpful?
                            </p>
                            <div className="flex items-center gap-2">
                              {votedFAQs.has(faq.id) ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span>Thanks for your feedback!</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleHelpfulRating(faq.id, true)}
                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                                    title="Yes, this was helpful"
                                  >
                                    <ThumbsUp className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleHelpfulRating(faq.id, false)}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                                    title="No, this wasn't helpful"
                                  >
                                    <ThumbsDown className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>

          {/* Stylish "View All FAQs" Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="relative inline-block">
              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 rounded-2xl blur-xl scale-110"></div>
              
              <Button asChild className="relative bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-xl hover:shadow-2xl transition-all duration-300 px-8 py-4 rounded-2xl text-lg font-semibold group">
                <Link to="/faq" className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span>More FAQs</span>
                  </div>
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </Button>
            </div>
            
            <p className="mt-4 text-sm text-gray-500 flex items-center justify-center gap-2">
              <MessageCircle className="h-4 w-4 hidden md:inline" />
              <span>Find answers to all your questions about shipping, orders, and more</span>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LandingFAQ; 